import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Mail, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
    const { orderNumber } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/orders/lookup/${orderNumber}`)
            .then((r) => setOrder(r.data))
            .finally(() => setLoading(false));
    }, [orderNumber]);

    return (
        <div className="pt-32 pb-24 min-h-screen" data-testid="order-success-page">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <div className="w-20 h-20 rounded-full bg-pep-primary/10 text-pep-primary flex items-center justify-center mx-auto mb-6">
                        <Check className="w-8 h-8" />
                    </div>
                    <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Order Placed</p>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-pep-navy font-medium">
                        Thank you for your order.
                    </h1>
                    <p className="text-pep-muted mt-4 text-lg">
                        We've sent a confirmation with payment instructions to your inbox.
                    </p>
                </div>

                {loading ? (
                    <div className="rounded-3xl bg-pep-surface h-64 animate-pulse" />
                ) : order ? (
                    <div className="bg-white rounded-3xl border border-pep-border/50 p-8" data-testid="order-details">
                        <div className="flex justify-between flex-wrap gap-4 mb-6 pb-6 border-b border-pep-border/50">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-pep-muted">Order number</p>
                                <p className="font-heading text-2xl text-pep-navy" data-testid="order-number">{order.order_number}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-widest text-pep-muted">Total</p>
                                <p className="font-heading text-2xl text-pep-primary" data-testid="order-total">${order.total.toFixed(2)}</p>
                            </div>
                        </div>

                        <div className="space-y-3 mb-6">
                            {order.items.map((i, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                    <span className="text-pep-navy">{i.name} × {i.quantity}</span>
                                    <span className="text-pep-muted">${(i.price * i.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                            {order.discount_amount > 0 && (
                                <div className="flex justify-between text-sm text-pep-primary pt-2 border-t border-pep-border/50">
                                    <span>Bundle discount ({order.bundle_discount_percent}%)</span>
                                    <span>−${order.discount_amount.toFixed(2)}</span>
                                </div>
                            )}
                            {typeof order.shipping_cost === "number" && (
                                <div className="flex justify-between text-sm text-pep-muted">
                                    <span>Shipping</span>
                                    <span>{order.shipping_cost === 0 ? "FREE" : `$${order.shipping_cost.toFixed(2)}`}</span>
                                </div>
                            )}
                        </div>

                        <div className="p-5 rounded-2xl bg-pep-surface border-l-4 border-pep-primary mb-6">
                            <div className="flex items-start gap-3">
                                <Mail className="w-5 h-5 text-pep-primary mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-pep-navy leading-relaxed">
                                    <strong>Bank transfer instructions sent to {order.customer.email}.</strong>
                                    <br />
                                    <span className="text-pep-muted">
                                        Please use <strong className="text-pep-navy">{order.order_number}</strong> as your payment reference.
                                        Your order will ship within 24 hours of payment confirmation.
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <Link
                                to={`/track?o=${order.order_number}&e=${encodeURIComponent(order.customer.email)}`}
                                className="inline-flex items-center gap-2 bg-pep-primary text-white rounded-full px-6 py-3 font-medium hover:bg-pep-navy transition"
                                data-testid="order-track-btn"
                            >
                                <FileText className="w-4 h-4" /> Track Order
                            </Link>
                            <Link
                                to="/all"
                                className="inline-flex items-center gap-2 bg-white text-pep-navy border border-pep-border rounded-full px-6 py-3 font-medium hover:border-pep-primary hover:text-pep-primary transition"
                                data-testid="order-continue-shopping"
                            >
                                Continue shopping
                            </Link>
                        </div>
                    </div>
                ) : (
                    <p className="text-center text-pep-muted">Order not found.</p>
                )}
            </div>
        </div>
    );
}
